import { ChangeDetectorRef, Directive, forwardRef, Host, Injector, Provider } from '@angular/core';
import { AfterViewInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { WithSubscription } from '@shared/base';
import {
  formatDate,
  getLocale,
  isAfter,
  isBefore,
  isDate,
  isDateValid,
  isSame,
  parseDate,
  utcAsLocal
} from 'ngx-bootstrap/chronos';
import { BsDatepickerDirective, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { DatepickerComponent } from '../components/datepicker/datepicker.component';

const DATEPICKER_CONTRO_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatepickerValueAcessorDirective),
  multi: true
};

const DATEPICKER_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => DatepickerValueAcessorDirective),
  multi: true
};

@Directive({
    // tslint:disable-next-line: directive-selector
    selector: 'gcc-datepicker[bsDatepicker]',
    providers: [DATEPICKER_CONTRO_VALUE_ACCESSOR, DATEPICKER_VALIDATOR],
    standalone: false
})
export class DatepickerValueAcessorDirective extends WithSubscription()
  implements AfterViewInit, ControlValueAccessor, Validator {
  private value: Date;
  private ngControl: NgControl;
  constructor(
    @Host() public picker: BsDatepickerDirective,
    @Host() public dpc: DatepickerComponent,
    private localeService: BsLocaleService,
    private changeDetection: ChangeDetectorRef,
    private inj: Injector
  ) {
    super();

    this.subs.add(
      this.localeService.localeChange.subscribe(() => {
        this._setViewValue(this.value);
        this.changeDetection.markForCheck();
      })
    );

    this.subs.add(
      this.picker.bsValueChange.subscribe((value: Date) => {
        value = value && !this.value ? new Date(value.setHours(0, 0, 0, 0)) : value;
        this._setViewValue(value);
        if (this.value !== value) {
          this.value = value;
          this._onChange(value);
          this._onTouched();
        }
        this.changeDetection.markForCheck();
      })
    );
  }

  public ngAfterViewInit(): void {
    this.ngControl = this.inj.get(NgControl);

    this.subs.add(
      this.ngControl.control.valueChanges.subscribe(value => {
        if (!this.value) {
          this.value = value;
          this._setViewValue(value);
        }
        if (isSame(value, this.value, 'date') && !isSame(value, this.value, 'milliseconds')) {
          this.value = new Date(value);
          this.picker.bsValue = this.value;
          this._onChange(value);
        }
      })
    );
  }

  public registerOnChange(fn: () => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public writeValue(value: Date | string) {
    if (!value) {
      this.value = null;
    } else {
      const localeKey = this.localeService.currentLocale;
      const locale = getLocale(localeKey);
      if (!locale) {
        throw new Error(`Locale "${localeKey}" is not defined, please add it with "defineLocale(...)"`);
      }

      this.value = parseDate(value, this.picker._config.dateInputFormat, this.localeService.currentLocale);

      if (this.picker._config.useUtc) {
        this.value = utcAsLocal(this.value);
      }
    }

    this.picker.bsValue = this.value;
  }

  public validate(c: AbstractControl): ValidationErrors | null {
    const value: Date | string = c.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (isDate(value)) {
      const isValid = isDateValid(value);
      if (!isValid) {
        return { invalid: true };
      }

      if (this.picker && this.picker.minDate && isBefore(value, this.picker.minDate, 'date')) {
        // this.writeValue(this.picker.minDate);

        return { minDate: true };
      }

      if (this.picker && this.picker.maxDate && isAfter(value, this.picker.maxDate, 'date')) {
        // this.writeValue(this.picker.maxDate);

        return { maxDate: true };
      }
    }
  }

  private _setViewValue(value: Date): void {
    this.dpc.viewValue = !value
      ? ''
      : formatDate(value, this.picker._config.dateInputFormat, this.localeService.currentLocale);
  }

  private _onChange: (value: Date) => void = () => {};
  private _onTouched: () => void = () => {};
}
